import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import {PrinterSetting, PrinterType} from '../_models/store';
import { SampleOrder } from '../configuration/receipt-templates/sample-order';

/** Body of POST /backend/v2/printer/{pk}/test_print/ on success. */
export interface TestPrintResult {
  status: string;
  printer: string;
  template: string;
}

/** Body of POST /backend/v2/printer/add/ (Configuration -> Printer settings -> Add printer). */
export interface AddPrinterPayload {
  store: number;
  printer_id: string;
  name: string;
  printer_type: PrinterType;
  /** Terminal id; STATION printers only. */
  station?: number | null;
  occasion?: number[];
  menu_category?: number[];
  tableside_table?: number[];
  has_kitchen_format?: boolean;
  kitchen_language?: string;
  is_default?: boolean;
  kitchen_count?: number;
}

/** GET /backend/v2/printer/sample/{storeId}/ (Receipt templates preview, ENG-5886). The types live beside the
 *  preview renderer so it stays Angular-free; they are re-exported here for callers of getSample. */
export type { SampleGroup, SampleLine, SampleModifier, SampleMoney, SampleOrder } from '../configuration/receipt-templates/sample-order';

@Injectable({ providedIn: 'root' })
export class PrinterSettingsService {
  endPoint = environment.apiUrl;
  attendantEndpointV2 = `${this.endPoint}/backend/v2/printer`;

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type':  'application/json',
    })
  };

  constructor(private http: HttpClient){}

  getPrinterSettings(storeID:string){
    return this.http.get<PrinterSetting[]>(`${this.attendantEndpointV2}/${storeID}/`)
  }

  updatePrinterSetting(payload){
    return this.http.post<PrinterSetting>(`${this.attendantEndpointV2}/`, JSON.stringify(payload), this.httpOptions)
  }

  /** Creates one printer. 201 with the new printer; 409 (`code` duplicate / duplicate_other_store /
   *  station_taken) when the ID or station is already taken; 400 with `detail` otherwise. */
  addPrinter(payload: AddPrinterPayload){
    return this.http.post<PrinterSetting>(`${this.attendantEndpointV2}/add/`, JSON.stringify(payload), this.httpOptions)
  }

  /** Prints one template's sample on a store printer (Receipt templates -> Print test). 200 with the printer
   *  name; 4xx/502 with `detail` (no access, printer not of this store, bad template, printer server down). */
  testPrint(printerPk: number, template: string, sample: string): Observable<TestPrintResult> {
    return this.http.post<TestPrintResult>(`${this.attendantEndpointV2}/${printerPk}/test_print/`,
      JSON.stringify({ template, sample }), this.httpOptions)
  }

  /** The store's sample order for the Receipt templates preview: real menu items and money computed the way a
   *  live order is. 200 with the order (`source` says whether it came from the menu); 4xx with `detail`. */
  getSample(storeId: number | string): Observable<SampleOrder> {
    return this.http.get<SampleOrder>(`${this.attendantEndpointV2}/sample/${storeId}/`, this.httpOptions)
  }

}

